import axios from 'axios';
import type {
  StockScore,
  DetailedMetrics,
  ScoreHistory,
  Industry,
  IndustryStatistics,
  Tag,
  NewsArticle,
  Sentiment,
} from '../types/api';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Stock Management APIs
export const stockApi = {
  addStocks: async (symbols: string[]) => {
    const response = await api.post('/add-stocks', { symbols });
    return response.data;
  },

  fetchData: async (symbol: string) => {
    const response = await api.post(`/fetch-data?symbol=${symbol}`);
    return response.data;
  },

  calculateScores: async () => {
    const response = await api.post('/calculate-scores');
    return response.data;
  },

  getDetailedMetrics: async (): Promise<DetailedMetrics[]> => {
    const response = await api.get<DetailedMetrics[]>('/detailed-metrics');
    return response.data;
  },

  getFourScores: async (): Promise<StockScore[]> => {
    const response = await api.get('/four-scores');
    // Handle response format: { success: true, data: [...] }
    const result = response.data;
    
    let stocks: any[] = [];
    
    if (result && result.data && Array.isArray(result.data)) {
      stocks = result.data;
    } else if (Array.isArray(result)) {
      stocks = result;
    } else {
      console.warn('Unexpected API response format:', result);
      return [];
    }
    
    // Transform and enrich the data
    return stocks.map((stock) => ({
      ...stock,
      id: stock.symbol, // Use symbol as ID
    }));
  },

  getScoreHistory: async (symbol?: string): Promise<ScoreHistory[]> => {
    const url = symbol ? `/score-history?symbol=${symbol}` : '/score-history';
    const response = await api.get<ScoreHistory[]>(url);
    return response.data;
  },

  getStockDetail: async (symbol: string) => {
    const response = await api.get(`/stocks/${symbol}`);
    return response.data.data; // Returns the nested data object
  },
};

// Industry APIs
export const industryApi = {
  getAll: async (): Promise<Industry[]> => {
    const response = await api.get<Industry[]>('/industries');
    return response.data;
  },

  create: async (data: { name: string; description?: string }) => {
    const response = await api.post('/industries', data);
    return response.data;
  },

  getStatistics: async (): Promise<IndustryStatistics[]> => {
    const response = await api.get<IndustryStatistics[]>('/industries/statistics');
    return response.data;
  },
};

// Tag APIs
export const tagApi = {
  getAll: async (): Promise<Tag[]> => {
    const response = await api.get<Tag[]>('/tags');
    return response.data;
  },

  create: async (data: { name: string; color?: string; description?: string }) => {
    const response = await api.post('/tags', data);
    return response.data;
  },

  applyRules: async () => {
    const response = await api.post('/tags/apply-rules');
    return response.data;
  },

  getByAsset: async (symbol?: string): Promise<any> => {
    const url = symbol ? `/tags/by-asset?symbol=${symbol}` : '/tags/by-asset';
    const response = await api.get(url);
    return response.data;
  },
  
  getAllAssetsWithTags: async () => {
    const response = await api.get('/tags/by-asset');
    // Handle response format: { assets: [...] }
    const result = response.data;
    return result.assets || result.data || result;
  },
};

// News & Sentiment APIs
export const newsApi = {
  fetchNews: async (symbol: string): Promise<NewsArticle[]> => {
    const response = await api.get<NewsArticle[]>(`/fetch-news?symbol=${symbol}`);
    return response.data;
  },

  fetchSentiment: async (symbol: string): Promise<Sentiment> => {
    const response = await api.get<Sentiment>(`/fetch-sentiment?symbol=${symbol}`);
    return response.data;
  },
};

// Admin/Debug APIs
export const adminApi = {
  getRawResponse: async (symbol: string) => {
    const response = await api.get(`/raw-response?symbol=${symbol}`);
    return response.data;
  },

  getScoringRules: async () => {
    const response = await api.get('/scoring-rules');
    return response.data;
  },
};

export default api;

